/**
 * The ProfileMenu's QUICK-ACTION ORDER (kitecorp/transferify-ui#520) — which fixed rows the avatar
 * popover offers, and in what order.
 *
 * This lives outside React (epic #26 / `logic-outside-react`) for one concrete reason: the owner's
 * rule is "Logout is the last row, always", and a rule expressed only as the order of JSX siblings
 * cannot be asserted by anything — this repo has no UI-component tests, so a reorder during a restyle
 * would ship unnoticed. As a table it is one testable list of accessible NAMES in order, which is
 * exactly the guarantee the rule is about.
 *
 * The component RENDERS from this array rather than restating it, so the pin and the screen cannot
 * drift: a row added to the JSX without an entry here does not appear at all.
 *
 * It holds no copy: callers pass the localized labels in, the way `appDestinations` does, which is
 * what keeps the menu translatable.
 */

/** Catalog key for a quick action's label — `messages.account.menu[key]`, `upgradeTo` for Upgrade. */
export type ProfileMenuActionKey = "upgrade" | "account" | "analytics" | "billing" | "admin" | "logout";

/**
 * The rows' localized labels. `upgrade` names the tier the row opens (kitecorp/transferify-ui#3701) and is
 * null at the top of the ladder, where there is no tier to offer and so no row: one input, so a label and a
 * separate "can upgrade" flag cannot disagree.
 */
export type ProfileMenuLabels = Record<Exclude<ProfileMenuActionKey, "upgrade">, string> & {upgrade: string | null};

/** One quick-action row, ready to render: the view decides how it looks, never what it offers. */
export interface ProfileMenuAction {
	/** The locale-independent identity (stable React key + the view's icon/handler lookup). */
	key: ProfileMenuActionKey;
	/** The visible text, which is also the row's accessible name — the icons beside it are decorative. */
	label: string;
}

/**
 * The rows in menu order, Logout last. Two rows are conditional. Upgrade is shown to tiers with a
 * higher self-serve plan available (ui#167, ui#189), labelled with that plan, and omitted at the top of
 * the ladder, where {@link ProfileMenuLabels.upgrade} is null. Admin
 * (ui#1882) is shown when `/account` reported the caller's OWN operator role and sits directly above
 * Logout, the owner's placement; the rest of the menu is byte for byte what it is without it, so a
 * non-operator sees no entry, no placeholder and no disabled row.
 *
 * Admin is a SHORTCUT, not a permission check. Being an operator is decided by the server, which
 * answers 403 to anyone without the role (`pages/AdminRoute.tsx` says why a client-side copy of that
 * rule would be a weaker second one); this table only decides whether the row is offered, from the
 * caller's own `admin` flag, which leaks nobody else's status.
 *
 * Analytics sits between Account and Logout (ui#1522) and is offered to EVERY signed-in tier. It used
 * to be a tab on the account page; the owner moved it here so the dashboards get a sheet of their own.
 * It is unconditional because the api, not this table, decides what a plan may embed: a Free account
 * gets the basic dashboard and an upsell where the advanced one would be, so a plan condition here
 * would hide a surface those tiers can genuinely use.
 *
 * Billing follows it directly (ui#1791, the owner's placement), travelling the same road for the same
 * reason: it was the account page's second tab, and removing it left one segment in a pill that then had
 * nothing to switch between. It is unconditional too, and pointedly so — a Free account's billing sheet
 * is where that account subscribes and where its referral credit is claimed, so hiding the row from the
 * tier most likely to want it would be exactly backwards. Note it is NOT the same offer as Upgrade
 * above: Upgrade opens the next plan's checkout, this opens the account's own plan, payments and invites.
 *
 * Support is deliberately absent, and so is Advertise. Both left for the same reason: this menu is
 * behind a login, and neither is a signed-in concern. Support now has a single home in the Resources
 * menu (#520); "Advertise with us" moved to that menu's own Advertising column (owner 2026-08-17),
 * which renders in both auth states — an offer sold to people who have never used the product cannot
 * be reachable only after they sign up.
 */
export function profileMenuActions(
	labels: ProfileMenuLabels,
	options: {showsAdminShortcut: boolean},
): ProfileMenuAction[] {
	const upgrade: ProfileMenuAction[] = labels.upgrade === null ? [] : [{key: "upgrade", label: labels.upgrade}];
	const rest: Exclude<ProfileMenuActionKey, "upgrade">[] = [
		"account", "analytics", "billing",
		...(options.showsAdminShortcut ? ["admin" as const] : []),
		"logout",
	];
	return [...upgrade, ...rest.map(key => ({key, label: labels[key]}))];
}
